import { useSyncExternalStore } from 'react'

/* Hand-rolled ChangeNotifier — deliberately NOT zustand/redux.
 *
 * Both original stores (AppStateNotifier, FFAppState) rely on MANUAL,
 * selective notification: many mutations are silent and only explicit
 * notifyListeners() publishes. A conventional reactive store notifies on
 * every write, which would change observable behaviour (see spec §9.1).
 * This base gives us the exact Flutter semantics in ~40 lines. */

export class Notifier {
  private listeners = new Set<() => void>()
  /** Bumped by notifyListeners(); the stable snapshot for React. */
  private version = 0

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb)
    return () => this.listeners.delete(cb)
  }

  getVersion = (): number => this.version

  protected notifyListeners(): void {
    this.version++
    for (const cb of [...this.listeners]) cb()
  }
}

/* React binding. The component re-renders ONLY when the notifier publishes
 * (version bump) — silent mutations are invisible until the next publish,
 * exactly like Flutter widgets watching a ChangeNotifier.
 *
 * The selector runs on each render to read CURRENT values, but it is the
 * version — not the selected value — that triggers re-render. */
export function useNotifier<T extends Notifier, R>(
  notifier: T,
  selector: (n: T) => R,
): R {
  useSyncExternalStore(notifier.subscribe, notifier.getVersion)
  return selector(notifier)
}
